import { useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { useLocale } from '@hooks/useLocale';
import PrismicImage from '@components/cms/PrismicImage';
import Button from '@components/ui/Button';
import Heading from '@components/ui/Heading';
import { PageType, PromoBoxItem } from '@adapters/prismic/types/slices';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { eePromoClickHandler, eePromoImpHandler } from '@utils/gtm';

import styles from './PromoBox.module.scss';

const PromoBox = ({
  item,
  isSecondaryPromoBox,
  trackPromos,
  pageType,
  sliceIndex,
  positionWithinSlice,
}: {
  item: PromoBoxItem;
  isSecondaryPromoBox?: boolean;
  trackPromos?: boolean;
  pageType: PageType;
  sliceIndex: number;
  positionWithinSlice: number;
}) => {
  const locale = useLocale();
  const ref = useRef<HTMLElement | null>(null);
  const isVisible = useObjectVisibility(ref);

  const {
    url,
    image_url: imageUrl,
    image,
    heading_black_text: headingBlackText,
    heading_red_text: headingRedText,
    red_text_next_line: redTextNextLine,
    body,
    cta_text: ctaText,
    first_cta_text: firstCtaText,
    second_cta_text: secondCtaText,
    first_cta_url: firstCtaUrl,
    second_cta_url: secondCtaUrl,
    'co-op_brand_attribute': coOpBrandAttribute = null,
  } = item;

  const promoId = headingRedText
    ? `${headingBlackText} ${headingRedText}`
    : headingBlackText;

  const promoName = isSecondaryPromoBox
    ? `${promoId}-${pageType}-PromoboxCarousel`
    : `${promoId}-${pageType}-PrimaryPromobox`;

  const promoDetails = useMemo(
    () => ({
      id: promoId,
      name: promoName,
      type: 'link',
      url: url,
      position: `${pageType}-${sliceIndex}-${
        isSecondaryPromoBox ? 'PromoboxCarousel' : 'PrimaryPromobox'
      }${positionWithinSlice}`,
      'co-op_brand_attribute': coOpBrandAttribute,
    }),
    [
      promoId,
      promoName,
      url,
      pageType,
      sliceIndex,
      isSecondaryPromoBox,
      positionWithinSlice,
      coOpBrandAttribute,
    ]
  );

  const handleClick = (ctaType: 'first_cta' | 'second_cta') => {
    eePromoClickHandler({
      ...promoDetails,
      url: ctaType === 'first_cta' ? firstCtaUrl : secondCtaUrl,
    });
  };

  useEffect(() => {
    if (trackPromos && isVisible) {
      eePromoImpHandler(promoDetails);
    }
  }, [isVisible, promoDetails, trackPromos]);

  if (!image) {
    return null;
  }

  const headingRedTextWrapper = headingRedText ? (
    <span className={styles.redText}>{`${' '}${headingRedText}`}</span>
  ) : (
    ''
  );

  return (
    <article
      ref={ref}
      className={cn(styles.boxContainer, {
        [styles.secondaryPromoBox]: isSecondaryPromoBox,
      })}
    >
      <div
        style={{
          aspectRatio: item.image.dimensions
            ? `auto ${item.image.dimensions.width} / ${item.image.dimensions.height}`
            : 'auto',
        }}
        className={styles.imageContainer}
      >
        {isSecondaryPromoBox ? (
          <PrismicImage src={image?.url} alt={image.alt ?? ''} layout="fill" />
        ) : (
          <a
            href={`/${locale}${imageUrl ?? url}`}
            onClick={() =>
              eePromoClickHandler({ ...promoDetails, type: 'image' })
            }
          >
            <PrismicImage
              src={image?.url}
              alt={image.alt ?? ''}
              layout="fill"
            />
          </a>
        )}
      </div>

      <div className={styles.contentContainer}>
        {headingBlackText && (
          <Heading semanticLevel="h3" styleLevel="3" className={styles.heading}>
            {headingBlackText}
            {redTextNextLine && <br />}
            {headingRedTextWrapper}
          </Heading>
        )}
        {body && <p className={styles.body}>{body}</p>}
        {(ctaText || firstCtaText || secondCtaText) && (
          <div className={styles.ctaContainer}>
            {(ctaText || firstCtaText) && (
              <Button
                type="button"
                size="large"
                variant="secondary"
                href={`/${locale}${firstCtaUrl ?? url}`}
                label={firstCtaText ?? ctaText}
                handleClick={() => handleClick('first_cta')}
              />
            )}
            {secondCtaText && (
              <Button
                type="button"
                size="large"
                variant="secondary"
                href={`/${locale}${secondCtaUrl}`}
                label={secondCtaText}
                handleClick={() => handleClick('second_cta')}
              />
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default PromoBox;
